import { useQuery } from '@apollo/client';
import { Box, CircularProgress, Collapse, ListSubheader, MenuItem, MenuList, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { requirePresent } from 'src/lib/requirePresent';
import type { GetConversationPage, GetConversationPageVariables } from 'types/graphql';
import AiChatApi from '../api/AiChat.api';
import { useLoadMessages } from '../hooks/useLoadMessages';
import { useAiChatStore } from '../store/aiChat.store';

type ConversationNode = {
  id: number;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  metadata: { title?: string };
};

type GroupedConversations = { label: string; conversations: ConversationNode[] };

const FIRST_CONVERSATION_PAGE: GetConversationPageVariables = {
  input: { first: 45, sortItems: [{ field: 'updatedAt', sort: 'desc' }] },
};

/**
 * Get the time-based group label for a date
 */
function getTimeGroupLabel(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
  const diffMonths = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());

  if (dateOnly.getTime() === today.getTime()) {
    return 'Today';
  }
  if (dateOnly.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }
  if (diffDays <= 7) {
    return 'Last 7 days';
  }
  if (diffDays <= 14) {
    return '2 weeks ago';
  }
  if (diffDays <= 21) {
    return '3 weeks ago';
  }
  if (diffDays <= 30) {
    return 'Last month';
  }
  if (diffMonths <= 2) {
    return '2 months ago';
  }
  if (diffMonths <= 3) {
    return '3 months ago';
  }
  // For older items, group by month/year
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Group conversations by time period
 */
function groupConversationsByTime(conversations: ConversationNode[]): GroupedConversations[] {
  const groups: Map<string, ConversationNode[]> = new Map();
  const groupOrder: string[] = [];

  for (const conv of conversations) {
    const date = new Date(conv.updatedAt || conv.createdAt);
    const label = getTimeGroupLabel(date);

    if (!groups.has(label)) {
      groups.set(label, []);
      groupOrder.push(label);
    }
    groups.get(label)!.push(conv);
  }

  return groupOrder.map((label) => ({ label, conversations: groups.get(label)! }));
}

export default function AiChatHistory() {
  const historyExpanded = useAiChatStore((state) => state.historyExpanded);
  const conversations = useAiChatStore((state) => state.conversationList);
  const appendConversationList = useAiChatStore((state) => state.appendConversationList);

  const groupedConversations = useMemo(() => groupConversationsByTime(conversations), [conversations]);

  const pagination = useRef<GetConversationPage['conversationPage']['pageInfo']>({
    endCursor: null,
    hasNextPage: true,
  });

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const { refetch: getConversations, loading } = useQuery<GetConversationPage, GetConversationPageVariables>(
    AiChatApi.GetConversationPage,
    {
      variables: FIRST_CONVERSATION_PAGE,
      onCompleted(data) {
        const edges = data?.conversationPage?.edges || [];
        const conversations = edges.map((edge: any) => edge.node);
        appendConversationList(conversations);
        pagination.current = data?.conversationPage?.pageInfo;
      },
    }
  );

  const { loadMessages } = useLoadMessages();
  const loadConversation = useAiChatStore((state) => state.loadConversation);
  const setConversationStatus = useAiChatStore((state) => state.setConversationStatus);
  const reset = useAiChatStore((state) => state.reset);

  const handleLoadConversation = useCallback(
    async (conv: ConversationNode) => {
      setConversationStatus('LOADING');
      try {
        const result = await loadMessages({ conversationId: conv.id, first: 20, sortDirection: 'desc' });

        const messages = result.messages.reverse();

        loadConversation(
          conv.id,
          messages,
          result.pageInfo.endCursor,
          result.pageInfo.hasNextPage,
          conv.title,
          conv.createdAt
        );
      } catch (e) {
        console.error('Error loading conversation:', e);
        setConversationStatus('ERROR');
      }
    },
    [loadMessages, loadConversation, setConversationStatus]
  );

  const loadMoreConversations = useCallback(async () => {
    const { endCursor, hasNextPage: hasMore } = pagination.current ?? {};

    if (!hasMore || loading || !endCursor) return;

    await getConversations({
      input: { first: 10, after: endCursor, sortItems: [{ field: 'updatedAt', sort: 'desc' }] },
    });
  }, [loading, getConversations]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom && requirePresent(pagination.current, 'hasNextPage').hasNextPage && !loading) {
      loadMoreConversations();
    }
  }, [loading, loadMoreConversations]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const theme = useTheme();

  return (
    <Collapse in={historyExpanded} orientation="horizontal" sx={{ flexShrink: 0 }}>
      <MenuList
        ref={scrollContainerRef}
        component="div"
        sx={{
          height: '100%',
          overflowY: 'auto',
          width: 250,
          flexShrink: 0,
          borderRight: `1px solid ${theme.palette.divider}`,
          py: 0,
          pb: '36px',
        }}
      >
        <Box
          sx={{ position: 'sticky', top: 0, zIndex: 2, pt: '12px', bgcolor: theme.palette.background.paper, mb: '4px' }}
        >
          <MenuItem onClick={() => reset()}>New Chat</MenuItem>
        </Box>
        {groupedConversations.length > 0 ? (
          groupedConversations.map((group) => (
            <Box key={group.label}>
              <ListSubheader
                sx={{
                  position: 'sticky',
                  top: 36,
                  zIndex: 1,
                  bgcolor: theme.palette.background.paper,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  lineHeight: '32px',
                  px: 2,
                  pt: 1,
                }}
              >
                {group.label}
              </ListSubheader>
              {group.conversations.map((conv) => (
                <MenuItem
                  key={conv.id}
                  onClick={() => handleLoadConversation(conv)}
                  sx={{
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    ...(conv.title ? {} : { fontStyle: 'italic', color: theme.palette.text.disabled }),
                  }}
                >
                  {conv.title || 'Untitled Conversation'}
                </MenuItem>
              ))}
            </Box>
          ))
        ) : loading ? (
          <MenuItem disabled>
            <CircularProgress size={20} />
          </MenuItem>
        ) : (
          <MenuItem disabled>No recent chats</MenuItem>
        )}
        {loading && conversations.length > 0 && (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <CircularProgress size={20} />
            </Box>
          </MenuItem>
        )}
      </MenuList>
    </Collapse>
  );
}
